import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { MapComponent } from '@elfsight/widget-map';
import { createReadableClass, getColorWithAlpha } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useActiveLocation } from '../active-location-context';
import { isDisplayable } from '../components/displayable';
import { LocationTags } from '../components/location-tags';

const GM_PLACE_URL = 'https://www.google.com/maps/search';

export const DetailsFooter = ({ showMap }: { showMap: boolean }) => {
  const {
    detailCardElements,
    language,
    mapColorScheme,
    mapMarkerColor,
    mapMarkerIcon,
    textColor,
    widgetToken
  } = useSettings();
  const { activeLocation } = useActiveLocation();

  const { language: locale } = language ?? {};

  if (!activeLocation?.place) {
    return null;
  }
  const locationLink = `${GM_PLACE_URL}/"${encodeURIComponent(activeLocation.place.name)}", ${encodeURIComponent(activeLocation.place.address)}`;

  const isDetailDisplayable = isDisplayable(detailCardElements);
  const showTags = isDetailDisplayable('tags', activeLocation?.tags?.length);

  if (!showTags && !showMap) {
    return null;
  }

  return (
    <Inner>
      <Show when={showTags}>
        <Divider _color={getColorWithAlpha(textColor, 0.1)} />
        <LocationTags tags={activeLocation.tags} />
      </Show>

      <Show when={showMap}>
        <a href={locationLink} target="_blank" rel="noreferrer">
          <MapComponentContainer
            staticMode
            zoom={13}
            center="auto"
            mapStyle={mapColorScheme}
            points={[
              {
                id: activeLocation?.id,
                coordinates: activeLocation.place?.coordinates
              }
            ]}
            height="200px"
            locale={locale}
            markerColor={mapMarkerColor}
            markerIconUrl={mapMarkerIcon?.url}
            widgetToken={widgetToken}
          />
        </a>
      </Show>
    </Inner>
  );
};

const Inner = styled.div.attrs({
  className: createReadableClass({
    component: 'details-footer',
    element: 'inner'
  })
})`
  display: flex;
  gap: 32px;
  flex-direction: column;
`;

const Divider = styled.div.attrs({
  className: createReadableClass({
    component: 'details-footer',
    element: 'divider'
  })
})<{ _color: string }>`
  width: 100%;
  height: 1px;
  background: ${({ _color }) => _color};
`;

const MapComponentContainer = styled(MapComponent)`
  border-radius: 12px;
  overflow: hidden;
`;
